import {
  calculateEstimate,
  createPaymentSourceBankAuthorize,
  createPaymentSourceBankComplete,
  createPaymentSourceCard,
  createPaymentSourceCardSetup,
  createPaymentSourceInvoice,
  createPaymentSourcePaypalAuthorize,
  createPaymentSourcePaypalComplete,
  getHardwareShippingRates,
  getPlan,
  listCharges,
  getSubscription,
  searchPlans,
  startSubscription,
  updateCustomerAddress,
  updateCustomerVat,
  updateSubscription,
  updateSubscriptionPoNumber,
  verifyAddress,
} from "src/billinglatest/clients/service.client";
import { ROUTES } from "src/constants/routes";
import { ALL_CURRENCY_CODES } from "../steps/PlanBillingStep/constants";
import { Device } from "../steps/CheckoutStep/shippingRates";
import { PIXI_RETURN_PATHS } from "../steps/CheckoutStep/purchaseResume";
import type { PixiBillingClient } from "./pixiBillingClient";

/**
 * The PIXI flow on billing v1: the `billinglatest` client calls the flow has always made, unchanged.
 *
 * @param spaceId - The space the v1 calls are scoped to.
 */
export const createV1PixiBillingClient = (
  spaceId?: string,
): PixiBillingClient => ({
  version: "v1",
  billingPath: ROUTES.ACCOUNT_LATEST,
  returnPaths: PIXI_RETURN_PATHS,

  getCurrency: async () => {
    const plan = await getPlan(spaceId);
    return plan?.currency?.toUpperCase();
  },
  listPlans: () =>
    searchPlans({ version: "v4", currencyCode: ALL_CURRENCY_CODES }),
  listShippingRates: () => getHardwareShippingRates(Device.Pixi),
  listDeviceCharges: (itemId) => listCharges({ itemId }),
  calculateEstimate: (request) => calculateEstimate(request, spaceId),
  // Unscoped, as the wizard has always called it.
  verifyAddress: (request) => verifyAddress(request),

  updateBillingAddress: async (address) => {
    await updateCustomerAddress(address, spaceId);
  },
  updateVatNumber: async (vatNumber) => {
    await updateCustomerVat({ vatNumber }, spaceId);
  },

  startCardSetup: async (paymentMethodId) => {
    const intent = await createPaymentSourceCardSetup(
      { paymentMethodId },
      spaceId,
    );
    return intent
      ? { id: intent.id, secret: intent.secret, status: intent.status }
      : undefined;
  },
  completeCardSetup: async (token) => {
    await createPaymentSourceCard({ token }, spaceId);
  },
  authorizePaypal: async (urls) => {
    const response = await createPaymentSourcePaypalAuthorize(urls, spaceId);
    return { redirectUrl: response?.redirectUrl, token: response?.token };
  },
  completePaypal: async (token) => {
    await createPaymentSourcePaypalComplete({ token }, spaceId);
  },
  authorizeBank: async (urls) => {
    const response = await createPaymentSourceBankAuthorize(urls, spaceId);
    return {
      redirectUrl: response?.redirectUrl,
      token: response?.redirectFlowId,
    };
  },
  completeBank: async (token) => {
    await createPaymentSourceBankComplete({ redirectFlowId: token }, spaceId);
  },
  setInvoicePayment: async () => {
    await createPaymentSourceInvoice(spaceId);
  },

  activate: async ({ planId, quantity, bundle }) => {
    // Set the plan + licences, except on a cancelled subscription: updating one reactivates AND invoices it, billing
    // at the catalog price before the start call can apply the campaign price (GRWBKK-1963). Read fresh: the
    // redirect-resume path replays an order persisted before the gateway hop. `getSubscription` yields `{}` on
    // error, so an unreadable status keeps the update-then-start path.
    const subscription = await getSubscription(spaceId);
    const isCancelled = subscription.status === "cancelled";
    // `paymentIntentId` is always null: the card is authenticated on its SetupIntent when attached and Chargebee
    // charges off-session on start.
    if (!isCancelled) {
      await updateSubscription(
        { planId, quantity, paymentIntentId: null },
        spaceId,
      );
    }
    // The free-device deal passes the bundle option + shipping; a cancelled subscription also carries the selection
    // the update skipped.
    const startRequest: NonNullable<Parameters<typeof startSubscription>[0]> = {
      ...(isCancelled ? { planId, quantity } : {}),
      ...(bundle
        ? {
            option: "pixi" as const,
            shippingAddress: bundle.shippingAddress,
            shippingService: bundle.shippingService,
          }
        : {}),
    };
    // A plain trial conversion sends no body at all.
    const hasStartRequest = Object.keys(startRequest).length > 0;
    await startSubscription(
      hasStartRequest ? startRequest : undefined,
      spaceId,
    );
  },
  updatePoNumber: async (poNumber) => {
    try {
      await updateSubscriptionPoNumber({ poNumber }, spaceId);
    } catch {
      // Non-fatal: the purchase is complete; the PO can be set later.
    }
  },
});
